import { useState } from 'react';
import { Plus } from 'lucide-react';
import { Button } from '@reflex/ui/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@reflex/ui/components/ui/dialog';
import { InviteEmailForm } from './InviteEmailForm';
import { ShareInviteList } from './ShareInviteList';
import { shareDialogCopy, type InviteShare } from './inviteShare';

interface AddMemberDialogProps {
  organizationId: string;
  /** `data-tutorial-id` for the send button — see {@link InviteEmailForm}. */
  tutorialId?: string;
}

/**
 * The members page's "Add member" action: an email invite.
 *
 * This used to be a picker over existing users, which on this page was always
 * empty — its list came from the org-scoped `useUsers()`, so every candidate
 * it could offer was already a member. Adding someone to an org you
 * administer means inviting them, so that is what the button does. (The
 * platform-admin org detail page keeps a picker: its user list is
 * org-agnostic, so it really can hold accounts that are not members yet.)
 *
 * One dialog does both steps. Sending swaps this body for the one-time links
 * rather than opening `ShareInviteDialog` on top: two Radix dialogs changing
 * state in the same commit race, and the closing layer can dismiss the
 * arriving one before it is ever painted.
 */
export function AddMemberDialog({ organizationId, tutorialId }: AddMemberDialogProps) {
  const [open, setOpen] = useState(false);
  const [shares, setShares] = useState<InviteShare[] | null>(null);
  const copy = shares && shares.length > 0 ? shareDialogCopy(shares) : null;

  // Every exit routes through here, including the footer button: a controlled
  // Radix dialog does not call `onOpenChange` for our own `setOpen(false)`, so
  // closing from the button alone would leave the spent links in state and
  // reopen straight onto them instead of a fresh form.
  const close = () => {
    setOpen(false);
    setShares(null);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (next) setOpen(true);
        else close();
      }}
    >
      <DialogTrigger asChild>
        <Button type="button" variant="outline" size="sm" aria-label="Add member">
          <Plus className="icon-sm" />
          Add member
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="add-member-dialog">
        <DialogHeader>
          <DialogTitle>{copy?.title ?? 'Add member'}</DialogTitle>
          <DialogDescription>
            {copy?.description ??
              'Invite someone by email. They join this organization when they redeem the link, whether or not they already have an account.'}
          </DialogDescription>
        </DialogHeader>
        {shares && shares.length > 0 ? (
          <>
            <ShareInviteList shares={shares} />
            <DialogFooter>
              <Button type="button" variant="secondary" onClick={close}>
                Done
              </Button>
            </DialogFooter>
          </>
        ) : (
          <InviteEmailForm
            organizationId={organizationId}
            tutorialId={tutorialId}
            onInvited={setShares}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
